import { _notNil, getJwt, getJwtPayload, jwtValid } from '@/littledash.ts';
import { currentReturnTo, isAuthRoute, logout } from '@/support/auth.ts';
import { ApplicationEvents } from '@/utils/ApplicationEvents.ts';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const ExpiredJwtDetector: FC = () => {
  const [authenticated, updateAuthenticated] = useState(() => jwtValid());
  const location = useLocation();

  const checkJWT = useCallback(() => {
    const token = getJwt();
    if (_notNil(token) && !jwtValid(token)) {
      if (isAuthRoute(location.pathname)) {
        logout({ type: 'stale-token' });
      } else {
        logout({ type: 'session-expired', returnTo: currentReturnTo() });
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const abortController = new AbortController();
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') {
          checkJWT();
        }
      },
      { signal: abortController.signal, passive: true }
    );
    window.addEventListener('online', () => checkJWT(), { signal: abortController.signal, passive: true });
    ApplicationEvents.onAuthenticated(() => updateAuthenticated(true), abortController.signal);
    const initialCheckTimeoutId = setTimeout(() => {
      if (!abortController.signal.aborted) {
        checkJWT();
      }
    }, 200);

    return () => {
      abortController.abort();
      clearTimeout(initialCheckTimeoutId);
    };
  }, [checkJWT, updateAuthenticated]);

  useEffect(() => {
    if (authenticated) {
      const exp = getJwtPayload()?.exp;
      if (Number.isFinite(exp)) {
        const timeout = (exp as number) * 1000 - Date.now() + 30000;
        if (timeout > 0) {
          const timeoutId = setTimeout(() => checkJWT(), timeout);
          return () => {
            clearTimeout(timeoutId);
          };
        } else {
          checkJWT();
        }
      }
    }
  }, [authenticated, checkJWT]);

  return null;
};
